<template>
  <div
    id="app"
    class="w-screen h-screen mt-0"
    :class="{'bg-black': this.theme === 'light', 'bg-white': this.theme === 'dark'}"
    v-if="appPublished"
  >
    <!-- This component is required as polyfill due to tailwind weird compilation bugs causing js-based strings (concationation) not being recognised as actual classes -->
    <classes-polyfill />

    <!-- Loader -->
    <t-loader-fullscreen ref="loader" />

    <!-- Debug menu. Active only on development mode. Adds menu to fast navigation between routes -->
    <debug-menu v-if="debug" />

    <!-- Adds gradient to the top safe zone  -->
    <safe-zone v-if="!SafeZone._hidden" />

    <!-- Adds photo preview modal. Reacts to the vuex mutations -->
    <photo-preview />

    <!-- App's main view component -->
    <router-view :key="key" />

    <!-- Show installation prompt for users sitting from native browsers in android and iOS -->
    <installation-prompt ref="installationPrompt" v-if="!$helpers.isPwaOpened()" :platform="$helpers.isIOS() ? 'ios' : 'android'" />

    <!-- Show prompt to engage user to turn on push notifications (works only when logged in) -->
    <push-prompt v-if="userTokenAccess" />

    <!-- Handle push notifications -->
    <push-notifications v-if="(userTokenAccess || userTokenAdmin) && userMenu.length !== 0" />

    <!-- 404 error pop-up -->
    <t-modal ref="modal" :actionBtns="false" @hide="closePageNotFound()">
      <div class="flex flex-col gap-2">
        <t-heading tag="h2" class="font-semibold text-center" color="black">{{ $t('components.404.whoops') }}</t-heading>
        <t-text color="black">
          {{ $t('components.404.description') }}
        </t-text>
      </div>
      <t-button class="mt-2 font-semibold" color="branded" @click.native="closePageNotFound()">{{ $t('components.404.ok') }}</t-button>
    </t-modal>

    <!-- Error popup -->
    <t-modal :actionBtns="false" ref="popup" @hide="closePopup()">
      <div class="flex flex-col gap-2 text-black">
        <t-heading tag="h2" class="font-semibold text-center" color="black">{{ $t('auth.authWhoops') }}</t-heading>
        <t-text>{{ msg }}</t-text>
      </div>
    </t-modal>
  </div>

  <!-- App's disabled screen -->
  <t-app-unpublished v-else />
</template>

<script>
  import ClassesPolyfill from './utils/polyfills/ClassesPolyfill.vue'
  import InstallationPrompt from './components/InstallationPrompt.vue'
  import PushPrompt from './components/PushPrompt.vue'
  import PushNotifications from './components/PushNotifications.vue'
  import PhotoPreview from './components/PhotoPreview.vue'
  import AXIOS from '@/integrations/axios'
  import {API} from '@/integrations/axios'
  import {mapGetters} from 'vuex'
  import {mapMutations} from 'vuex'
  import {mapActions} from 'vuex'
  import handleViewport from '@/utils/mixins/handleViewport'

  export default {
    name: 'RootComponent',
    components: {
      ClassesPolyfill,
      InstallationPrompt,
      PushPrompt,
      PhotoPreview,
      PushNotifications
    },
    data() {
      return {
        swUpdating: false,
        debug: false,
        requests: [],
        key: 0,
        msg: null
      }
    },
    computed: {
      ...mapGetters(['themeColorHEX', 'appOauth', 'appPublished', 'appLoaded', 'configLoaded', 'theme', 'appLocale']),
      ...mapGetters('layout', ['SafeZone']),
      ...mapGetters('user', [
        'userTokenAccess',
        'userTokenAdmin',
        'userLoggedIn',
        'userActivity',
        'userFirstModule',
        'activityLoaded',
        'SW',
        'menuLoaded',
        'userMenu'
      ]),
      isDesktop() {
        return this.viewport.width >= this.$screen.md
      },
      isWideDesktop() {
        return this.viewport.width >= this.$screen.lg
      },
      isMobile() {
        return this.viewport.width < this.$screen.md
      }
    },
    methods: {
      ...mapMutations('user', [
        'setAdminToken',
        'addNewUserActivity',
        'setAccessToken',
        'deleteAdminToken',
        'deleteHideIosAlert',
        'deleteHideAndroidAlert',
        'handleUserBubble',
        'saveSW',
        'setNetworkingState'
      ]),
      ...mapMutations(['setAppLoadedState']),
      ...mapMutations('layout', ['toggleSafeZone', 'emitContentReload', 'setDynamicIsland']),
      ...mapActions(['getAppConfig']),
      ...mapActions('user', ['getUserMenu', 'getUserActivity', 'getUserNotifications']),
      async onWideDesktopViewEnter() {
        if (this.$route.name === 'activity.index') return
        if (this.userActivity.items.length === 0) await this.getUserActivity()
      },
      disableSmartZoom() {
        document.querySelectorAll('*').forEach(el => {
          el.addEventListener('dblclick', function (e) {
            if (!el.id) e.preventDefault()
          })
        })
      },
      routerAvaiable() {
        return new Promise(resolve => {
          const interval = setInterval(() => {
            if (this.$router && this.$router.push) {
              clearInterval(interval)
              resolve()
            }
          }, 333)
        })
      },
      pageNotFound() {
        this.$refs.modal.show()
      },
      goToRootModule() {
        this.$router.push({name: `${this.userFirstModule.type}.index`, params: {app_module_id: this.userFirstModule.app_module_id}})
      },
      closePageNotFound() {
        this.$refs.modal.hide()
        this.$router.push({name: `${this.userFirstModule.type}.index`, params: {app_module_id: this.userFirstModule.app_module_id}})
      },
      popup(msg) {
        this.msg = msg
        this.$refs.popup.show()
      },
      closePopup() {
        this.$refs.modal.hide()
        this.msg = null
      }
    },
    async beforeCreate() {
      document.querySelector('body').style.height = this.$helpers.isPwaOpened() ? '100vh' : 'calc(var(--1dvh) * 100)'
      document.querySelector('html').style.height = this.$helpers.isPwaOpened() ? '100vh' : 'calc(var(--1dvh) * 100)'
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          // see if the app is closed to tray
          this.$socket.emit('getBubbles')
          if (['networking.group', 'networking.speed', 'events.index', 'events.item'].includes(this.$route.name))
            window.location.reload()
          if (this.$route.name === 'chats.index') this.emitContentReload()
        }
      })
      await this.configLoaded
      document.querySelector('html').dataset.theme = this.theme
      if (navigator.serviceWorker) {
        navigator.serviceWorker.addEventListener('message', async event => {
          if (event.data.type === 'openUrl') {
            await this.routerAvaiable()
            const params = event.data.url.match(/[?&]pushAnalyticsId=([^&]*)/)
            if (params && params.length && params.length !== 0) AXIOS.put(`/push-open/${params[1]}`)
            this.$router.push(event.data.url.replace(/^(?:https?:\/\/)?[^/]+(\/[^?#]*)?.*$/, '$1'))
          }
        })
        navigator.serviceWorker.ready.then(SW => {
          this.saveSW(SW)
        })
      }
      this.toggleSafeZone()
      this.toggleSafeZone()
      if (this.$helpers.isPreviewFrame() && !this.userTokenAdmin) {
        window.addEventListener('message', event => {
          if (event.data.admin_token) {
            this.setAdminToken(event.data.admin_token)
            window.location.reload()
          }
        })

        parent.postMessage({requestToken: true}, this.appOauth.replace('//login.', '//admin.'))
      }
    },
    mixins: [handleViewport],
    async created() {
      await this.configLoaded
      if (!this.$helpers.isPreviewFrame() && this.userTokenAdmin) {
        this.deleteAdminToken()
      }
      if (this.$helpers.isPwaOpened()) {
        this.deleteHideAndroidAlert()
        this.deleteHideIosAlert()
      }
      await this.getAppConfig()
      if (this.appPublished === false) {
        this.setAppLoadedState(true)
        this.$refs.loader.toggleActive()
        return
      }
      if (this.userLoggedIn) {
        await this.getUserMenu()
        await this.menuLoaded
        this.$socket.emit('getBubbles')
        await this.getUserNotifications()
        if (this.isWideDesktop && this.$route.name !== 'activity.index') await this.getUserActivity()
      } else {
        if (this.$route.query['access-token']) this.setAccessToken(this.$route.query['access-token'])
      }
      this.setAppLoadedState(true)
      this.$refs.loader.toggleActive()
    },
    async mounted() {
      if (process.env.NODE_ENV == 'development') {
        this.debug = true
      }
      await this.appLoaded
      this.disableSmartZoom()
      if (this.appPublished === true) {
        let send = {}
        if (this.$helpers.isPwaOpened()) send.pwaAppMode = this.$helpers.isPwaOpened()
        if (this.$helpers.isPreviewFrame()) {
          send.isPreviewFrame = this.$helpers.isPreviewFrame()
        }
        send.deviceType = this.$helpers.getDeviceType()
        if (this.$helpers.isPreviewFrame() ? this.userTokenAdmin : this.userTokenAccess) {
          AXIOS.post('/pwa/audit', send).then(() => {
            this.$cookies.set('audit', true, 10)
            this.$eventBus.$emit('showMenu')
          })
        } else {
          API.post('/pwa/audit', send).then(() => {
            this.$cookies.set('audit', true, 10)
            this.$eventBus.$emit('showMenu')
          })
        }
      }
      this.$eventBus.$on('404', this.pageNotFound)
      this.$eventBus.$on('goToRootModule', this.goToRootModule)
      this.$eventBus.$on('popup', msg => this.popup(msg))
    },
    beforeDestroy() {
      this.$eventBus.$off('404', this.pageNotFound)
      this.$eventBus.$off('goToRootModule', this.goToRootModule)
      this.$eventBus.$off('popup', msg => this.popup(msg))
    },
    watch: {
      $route(newVal, oldVal) {
        if (
          newVal.params &&
          newVal.name &&
          newVal.params.app_module_id !== ((oldVal.params && oldVal.params.app_module_id) || 0) &&
          newVal.name.split('.')[0] === ((oldVal.name && oldVal.name.split('.')[0]) || '')
        )
          this.key++
        if (newVal.name === oldVal.name) this.key++
        if (oldVal.name === null && oldVal.path === '/') return
        this.$cookies.remove('opened_url')
      },
      appLocale(newVal) {
        this.$root.$i18n.locale = newVal
      }
    },
    sockets: {
      showBubble(data) {
        if (this.$route.params.id) data.items = data.items.filter(el => el !== this.$route.params.id)
        if (data.module.split('/')[0] === 'activity' && this.viewport.width > this.$screen.lg) return
        this.handleUserBubble(data)
      },
      refreshActivity({activity}) {
        this.addNewUserActivity(activity)
      },
      speedNetworking(data) {
        this.setNetworkingState(data)
      },
      networkingActivity(data) {
        this.setDynamicIsland({data, hidden: false})
      }
    }
  }
</script>

<style lang="postcss">
  @import '@/assets/static/scss/main.scss';
</style>
